import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/api';
import { TIMEZONES, getInitials } from '@ultisim/shared';

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  timezone: z.string(),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

export default function ProfilePage() {
  const { user, loadUser } = useAuthStore();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      timezone: user?.timezone || 'America/New_York',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const onProfileSubmit = async (data: ProfileFormData) => {
    setIsUpdating(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await api.put('/users/me', data);
      await loadUser();
      setSuccessMessage('Profile updated successfully');
    } catch (err) {
      setErrorMessage('Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    setIsChangingPassword(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await api.post('/users/me/change-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      setSuccessMessage('Password changed successfully');
      resetPassword();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change password';
      setErrorMessage(message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const initials = user ? getInitials(user.firstName, user.lastName) : '';

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Profile Settings</h1>
        <p className="text-dark-400 mt-1">Manage your account settings</p>
      </div>

      {successMessage && (
        <div className="p-4 bg-accent-500/10 border border-accent-500/20 rounded-lg text-accent-400 text-sm">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {errorMessage}
        </div>
      )}

      {/* Avatar section */}
      <div className="card">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-white">{initials}</span>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white">
              {user?.firstName} {user?.lastName}
            </h3>
            <p className="text-dark-400">{user?.email}</p>
            <p className="mt-1">
              <span className={`badge ${user?.role === 'admin' ? 'badge-primary' : 'badge-gray'}`}>
                {user?.role === 'admin' ? 'Administrator' : 'Employee'}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Profile form */}
      <div className="card">
        <h3 className="text-lg font-medium text-white mb-6">Personal Information</h3>

        <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="firstName" className="label">First Name</label>
              <input
                {...registerProfile('firstName')}
                type="text"
                id="firstName"
                className={`input ${profileErrors.firstName ? 'input-error' : ''}`}
              />
              {profileErrors.firstName && (
                <p className="mt-1.5 text-sm text-red-400">{profileErrors.firstName.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="label">Last Name</label>
              <input
                {...registerProfile('lastName')}
                type="text"
                id="lastName"
                className={`input ${profileErrors.lastName ? 'input-error' : ''}`}
              />
              {profileErrors.lastName && (
                <p className="mt-1.5 text-sm text-red-400">{profileErrors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="timezone" className="label">Timezone</label>
            <select
              {...registerProfile('timezone')}
              id="timezone"
              className="input"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={isUpdating}
            className="btn-primary"
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Password form */}
      <div className="card">
        <h3 className="text-lg font-medium text-white mb-6">Change Password</h3>

        <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-6">
          <div>
            <label htmlFor="currentPassword" className="label">Current Password</label>
            <input
              {...registerPassword('currentPassword')}
              type="password"
              id="currentPassword"
              className={`input ${passwordErrors.currentPassword ? 'input-error' : ''}`}
            />
            {passwordErrors.currentPassword && (
              <p className="mt-1.5 text-sm text-red-400">{passwordErrors.currentPassword.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="newPassword" className="label">New Password</label>
            <input
              {...registerPassword('newPassword')}
              type="password"
              id="newPassword"
              className={`input ${passwordErrors.newPassword ? 'input-error' : ''}`}
            />
            {passwordErrors.newPassword && (
              <p className="mt-1.5 text-sm text-red-400">{passwordErrors.newPassword.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="label">Confirm New Password</label>
            <input
              {...registerPassword('confirmPassword')}
              type="password"
              id="confirmPassword"
              className={`input ${passwordErrors.confirmPassword ? 'input-error' : ''}`}
            />
            {passwordErrors.confirmPassword && (
              <p className="mt-1.5 text-sm text-red-400">{passwordErrors.confirmPassword.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isChangingPassword}
            className="btn-primary"
          >
            {isChangingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
